import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isTokenValid, decodeToken } from '../components/tokenUtils';
import GridComboData from '../components/gridComboData';
import { comboDefSportAbilities } from '../components/gridcombodefs/gridComboDefSportAbilities';
import { comboDefProperties } from '../components/gridcombodefs/gridComboDefProperties';
import { comboDefUnitProps } from '../components/gridcombodefs/gridComboDefUnits';
import { comboDefOrganisations } from '../components/gridcombodefs/gridComboDefOrganisations';
import { comboDefGendersProps } from '../components/gridcombodefs/gridComboDefGenders';
import { comboDefGrades } from '../components/gridcombodefs/gridComboDefGrades';
import { comboDefTrainingGroups } from '../components/gridcombodefs/gridComboDefTrainingGroups';
import { comboDefActiveStatus } from '../components/gridcombodefs/gridComboDefActiveStatus';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children, language }) => {
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    const [userName, setUserName] = useState(() => localStorage.getItem('userName'));
    const [loginState, setLoginState] = useState(() => token && isTokenValid(token) ? 'LOGGEDIN' : 'LOGOUT');
    const [lastActivity, setLastActivity] = useState(() => Date.now());
    // Utoljára mikor próbáltuk (sikeresen vagy sem) frissíteni a tokent — ld. lent,
    // a "Token validation and auto-refresh" effect.
    const lastRefreshAttemptRef = useRef(Date.now());

    // User level and menu items
    const [userLevel, setUserLevel] = useState(() => {
        const storedToken = localStorage.getItem('token');
        if (storedToken && isTokenValid(storedToken)) {
            const decoded = decodeToken(storedToken);
            return decoded?.userLevel || null;
        }
        return null;
    });
    const [menuItems, setMenuItems] = useState([]);

    // Combo data states
    const [gendersComboData, setGendersComboData] = useState(null);
    const [gradesComboData, setGradesComboData] = useState(null);
    const [unitComboData, setUnitComboData] = useState(null);
    const [organisationsComboData, setOrganisationsComboData] = useState(null);
    const [sportAbilitiesComboData, setSportAbilitiesComboData] = useState(null);
    const [trainingGroupsComboData, setTrainingGroupsComboData] = useState(null);
    const [personsComboData, setPersonsComboData] = useState(null);
    const [statusComboData, setStatusComboData] = useState(null);
    const [propertiesComboData, setPropertiesComboData] = useState(null);

    const navigate = useNavigate();

    // Activity tracking - update on any user interaction
    useEffect(() => {
        if (loginState !== 'LOGGEDIN') return;

        const updateActivity = () => {
            setLastActivity(Date.now());
        };

        // Track various user activities
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        events.forEach(event => {
            window.addEventListener(event, updateActivity);
        });

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
        };
    }, [loginState]);

    // Token validation and auto-refresh effect
    useEffect(() => {
        if (loginState !== 'LOGGEDIN') return;

        const checkTokenValidity = async () => {
            if (token && !isTokenValid(token)) {
                console.log('Token expired, auto logout');
                // Inline logout to avoid dependency issues
                localStorage.removeItem('token');
                localStorage.removeItem('userName');
                setToken(null);
                setUserName(null);
                setLoginState('LOGOUT');
                setUserLevel(null);
                setMenuItems([]);
                setGendersComboData(null);
                setGradesComboData(null);
                setUnitComboData(null);
                setOrganisationsComboData(null);
                setSportAbilitiesComboData(null);
                setTrainingGroupsComboData(null);
                setPersonsComboData(null);
                setStatusComboData(null);
                setPropertiesComboData(null);
                navigate('/home');
            } else if (token && isTokenValid(token)) {
                // Amíg a felhasználó aktív (volt interakció az utolsó 5 percben),
                // 10 percenként frissítjük a tokent egy friss, teljes érvényességű
                // párra — így a munkamenet a folyamatos munka alatt gyakorlatilag
                // nem jár le, de tétlenség esetén a token a saját (1 órás)
                // lejáratáig fut le, ahogy kell.
                //
                // (Korábban ez csak a lejárat előtti utolsó 5 percben próbálkozott —
                // ha épp nem esett aktivitás abba a szűk ablakba, a token simán
                // lejárt akkor is, ha a user közben ténylegesen dolgozott.)
                const now = Date.now();
                const timeSinceActivity = now - lastActivity;
                const ACTIVITY_THRESHOLD = 5 * 60 * 1000; // "aktívnak" számít, ha 5 percen belül történt interakció
                const REFRESH_INTERVAL = 10 * 60 * 1000; // ilyen gyakran hosszabbítjuk meg aktív használat mellett

                const isActive = timeSinceActivity < ACTIVITY_THRESHOLD;
                const dueForRefresh = now - lastRefreshAttemptRef.current > REFRESH_INTERVAL;

                if (isActive && dueForRefresh) {
                    lastRefreshAttemptRef.current = now;
                    console.log('Aktív munkamenet, token hosszabbítása...');
                    try {
                        const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/token`, {}, {
                            headers: { token }
                        });
                        const newToken = response.data.token;
                        localStorage.setItem('token', newToken);
                        setToken(newToken);
                        console.log('Token frissítve');
                    } catch (error) {
                        console.error('Token frissítési hiba:', error);
                    }
                }
            }
        };

        checkTokenValidity();
        const interval = setInterval(checkTokenValidity, 30000);

        return () => clearInterval(interval);
    }, [token, loginState, lastActivity, navigate]);

    const loadComboData = useCallback(async (authToken) => {
        try {
            const comboUnitProps = { ...comboDefUnitProps, token: authToken, language };
            const comboAbilityProps = { ...comboDefSportAbilities, token: authToken, language };
            const comboOrganisationsProps = { ...comboDefOrganisations, token: authToken, language };
            const comboGendersPropsWithToken = { ...comboDefGendersProps, token: authToken, language };
            const comboGradesProps = { ...comboDefGrades, token: authToken, language };
            const comboTrainingGroupsProps = { ...comboDefTrainingGroups, token: authToken, language };
            const comboPersonsProps = {
                token: authToken,
                collection: 'persons_view_gridCombo',
                select: 'ID, FieldValue',
                language,
                top: '500',
                where: '',
                groupby: '',
                orderby: 'FieldValue',
                with0: 'true',
            };
            const comboActiveStatusProps = { ...comboDefActiveStatus, token: authToken, language };
            const comboPropertiesProps = { ...comboDefProperties, token: authToken, language };

            const [unitData, personsData, abilityData, organisationsData, gendersData, gradesData, trainingGroupsData, statusData, propertiesData] = await Promise.all([
                GridComboData(comboUnitProps),
                GridComboData(comboPersonsProps),
                GridComboData(comboAbilityProps),
                GridComboData(comboOrganisationsProps),
                GridComboData(comboGendersPropsWithToken),
                GridComboData(comboGradesProps),
                GridComboData(comboTrainingGroupsProps),
                GridComboData(comboActiveStatusProps),
                GridComboData(comboPropertiesProps)
            ]);

            setUnitComboData(unitData);
            setPersonsComboData(personsData);
            setSportAbilitiesComboData(abilityData);
            setOrganisationsComboData(organisationsData);
            setGendersComboData(gendersData);
            setGradesComboData(gradesData);
            setTrainingGroupsComboData(trainingGroupsData);
            setStatusComboData(statusData);
            setPropertiesComboData(propertiesData);
        } catch (error) {
            console.error('Error loading combo data:', error);
        }
    }, [language]);

    // Load menu items from backend
    const loadMenuItems = useCallback(async (authToken) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/menu`, {
                headers: { token: authToken, language }
            });
            setMenuItems(response.data.data || []);
        } catch (error) {
            console.error('Error loading menu items:', error);
            setMenuItems([]);
        }
    }, [language]);

    // Refresh menu items
    const refreshMenuItems = useCallback(async () => {
        if (!token) return;
        await loadMenuItems(token);
    }, [token, loadMenuItems]);

    // Load menu items on mount if already logged in
    useEffect(() => {
        if (loginState === 'LOGGEDIN' && token && menuItems.length === 0) {
            loadMenuItems(token);
        }
    }, [loginState, token, menuItems.length, loadMenuItems]);

    // Load combo data on mount if already logged in (pl. oldal-frissítés után, amikor a token
    // localStorage-ből áll vissza a login()-on kívül, így a combo state-ek üresen maradnának)
    useEffect(() => {
        if (loginState === 'LOGGEDIN' && token && trainingGroupsComboData === null) {
            loadComboData(token);
        }
    }, [loginState, token, trainingGroupsComboData, loadComboData]);

    const refreshSportAbilities = useCallback(async () => {
        if (!token) return;
        try {
            const comboAbilityProps = { ...comboDefSportAbilities, token, language };
            const abilityData = await GridComboData(comboAbilityProps);
            setSportAbilitiesComboData(abilityData);
        } catch (error) {
            console.error('Error refreshing sport abilities:', error);
        }
    }, [token, language]);

    const refreshProperties = useCallback(async () => {
        if (!token) return;
        try {
            const comboPropertiesProps = { ...comboDefProperties, token, language };
            const propertiesData = await GridComboData(comboPropertiesProps);
            setPropertiesComboData(propertiesData);
        } catch (error) {
            console.error('Error refreshing properties:', error);
        }
    }, [token, language]);

    const refreshOrganisations = useCallback(async () => {
        if (!token) return;
        try {
            const comboOrganisationsProps = { ...comboDefOrganisations, token, language };
            const organisationsData = await GridComboData(comboOrganisationsProps);
            setOrganisationsComboData(organisationsData);
        } catch (error) {
            console.error('Error refreshing organisations:', error);
        }
    }, [token, language]);

    const refreshPersons = useCallback(async () => {
        if (!token) return;
        try {
            const comboPersonsProps = {
                token,
                collection: 'persons_view_gridCombo',
                select: 'ID, FieldValue',
                language,
                top: '500',
                where: '',
                groupby: '',
                orderby: 'FieldValue',
                with0: 'true',
            };
            const personsData = await GridComboData(comboPersonsProps);
            setPersonsComboData(personsData);
        } catch (error) {
            console.error('Error refreshing persons:', error);
        }
    }, [token, language]);

    const refreshTrainingGroups = useCallback(async () => {
        if (!token) return;
        try {
            const comboTrainingGroupsProps = { ...comboDefTrainingGroups, token, language };
            const trainingGroupsData = await GridComboData(comboTrainingGroupsProps);
            setTrainingGroupsComboData(trainingGroupsData);
        } catch (error) {
            console.error('Error refreshing training groups:', error);
        }
    }, [token, language]);

    const refreshUnits = useCallback(async () => {
        if (!token) return;
        try {
            const comboUnitProps = { ...comboDefUnitProps, token, language };
            const unitData = await GridComboData(comboUnitProps);
            setUnitComboData(unitData);
        } catch (error) {
            console.error('Error refreshing units:', error);
        }
    }, [token, language]);

    const refreshGenders = useCallback(async () => {
        if (!token) return;
        try {
            const comboGendersProps = { ...comboDefGendersProps, token, language };
            const gendersData = await GridComboData(comboGendersProps);
            setGendersComboData(gendersData);
        } catch (error) {
            console.error('Error refreshing genders:', error);
        }
    }, [token, language]);

    const refreshGrades = useCallback(async () => {
        if (!token) return;
        try {
            const comboGradesProps = { ...comboDefGrades, token, language };
            const gradesData = await GridComboData(comboGradesProps);
            setGradesComboData(gradesData);
        } catch (error) {
            console.error('Error refreshing grades:', error);
        }
    }, [token, language]);

    const refreshStatus = useCallback(async () => {
        if (!token) return;
        try {
            const comboStatusProps = { ...comboDefActiveStatus, token, language };
            const statusData = await GridComboData(comboStatusProps);
            setStatusComboData(statusData);
        } catch (error) {
            console.error('Error refreshing status:', error);
        }
    }, [token, language]);

    const login = async (credentials) => {
        const UsersURL = `${process.env.REACT_APP_API_BASE_URL}/token`;

        try {
            const response = await axios.get(UsersURL, {
                headers: {
                    email: credentials.login,
                    password: credentials.password
                }
            });

            const newToken = response.data.token;
            // Store in localStorage for persistence
            localStorage.setItem('token', newToken);
            localStorage.setItem('userName', credentials.login);

            // Extract userLevel from token
            const decoded = decodeToken(newToken);
            setUserLevel(decoded?.userLevel || null);

            setToken(newToken);
            setUserName(credentials.login);
            setLoginState('LOGGEDIN');
            lastRefreshAttemptRef.current = Date.now();

            // Load combo data and menu items
            await Promise.all([
                loadComboData(newToken),
                loadMenuItems(newToken)
            ]);

            // Navigate to dashboard after successful login
            navigate('/dashboard');

            return { success: true };
        } catch (err) {
            console.error('Login error:', err);
            return {
                success: false,
                error: err.response?.data?.message || 'Login failed'
            };
        }
    };

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('userName');
        setToken(null);
        setUserName(null);
        setLoginState('LOGOUT');

        // Clear user level and menu items
        setUserLevel(null);
        setMenuItems([]);

        // Clear combo data
        setGendersComboData(null);
        setGradesComboData(null);
        setUnitComboData(null);
        setOrganisationsComboData(null);
        setSportAbilitiesComboData(null);
        setTrainingGroupsComboData(null);
        setPersonsComboData(null);
        setStatusComboData(null);
        setPropertiesComboData(null);

        navigate('/home');
    }, [navigate]);

    const setEditLoginState = () => {
        setLoginState('EDITLOGIN');
    };

    const value = {
        token,
        userName,
        loginState,
        isAuthenticated: loginState === 'LOGGEDIN',
        login,
        logout,
        setEditLoginState,
        setLoginState,
        // User level and menu items
        userLevel,
        menuItems,
        refreshMenuItems,
        // Combo data
        gendersComboData,
        setGendersComboData,
        refreshGenders,
        gradesComboData,
        setGradesComboData,
        refreshGrades,
        unitComboData,
        setUnitComboData,
        refreshUnits,
        organisationsComboData,
        setOrganisationsComboData,
        refreshOrganisations,
        sportAbilitiesComboData,
        setSportAbilitiesComboData,
        refreshSportAbilities,
        trainingGroupsComboData,
        setTrainingGroupsComboData,
        refreshTrainingGroups,
        personsComboData,
        setPersonsComboData,
        refreshPersons,
        statusComboData,
        setStatusComboData,
        refreshStatus,
        propertiesComboData,
        setPropertiesComboData,
        refreshProperties,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
